/**
 * Global watcher for the email-intake AI draft job.
 *
 * The backend runs the two Claude passes as a background job (long
 * threads take 60-120s), so the wizard no longer has to hold an HTTP
 * request open. This provider owns the POLLING - lifted out of
 * ShareProposal so staff can navigate to any other staff page while
 * the draft is being written and get a toast ("Reply draft ready -
 * Review draft") the moment it lands. Returning to /share resumes
 * the wizard at the draft step via the context the job carries
 * (mintResult + parsedMsg stay in-memory only - a full page refresh
 * loses them, matching the no-persistence design for email content).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_MS = 5 * 60 * 1000;
const POLL_MAX_MISSES = 6;

const DraftJobCtx = createContext({
  job: null,
  startDraftJob: async () => {},
  clearJob: () => {},
});

export function DraftJobProvider({ children }) {
  const [job, setJob] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Read the CURRENT pathname inside async poll callbacks without
  // re-subscribing the polling loop to route changes.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // {timer, jobId} - jobId guards against a superseded job's stale
  // timeout firing after a new job started.
  const pollRef = useRef({ timer: null, jobId: null });
  const ctxRef = useRef({ projectReference: "" });

  const stopPolling = useCallback(() => {
    if (pollRef.current.timer) clearTimeout(pollRef.current.timer);
    pollRef.current = { timer: null, jobId: null };
  }, []);

  const clearJob = useCallback(() => {
    stopPolling();
    setJob(null);
  }, [stopPolling]);

  const notifyDone = useCallback(() => {
    const ref = ctxRef.current.projectReference;
    const message = ref
      ? `Reply draft ready for ${ref}.`
      : "Reply draft is ready.";
    if (pathRef.current === "/share") {
      toast.success(message);
    } else {
      toast.success(message, {
        duration: 30000,
        action: {
          label: "Review draft",
          onClick: () => navigate("/share"),
        },
      });
    }
  }, [navigate]);

  const notifyError = useCallback(
    (msg) => {
      const message = msg || "Couldn't draft the reply.";
      if (pathRef.current === "/share") {
        toast.error(message);
      } else {
        toast.error(message, {
          duration: 30000,
          action: {
            label: "Back to Send",
            onClick: () => navigate("/share"),
          },
        });
      }
    },
    [navigate],
  );

  const failJob = useCallback(
    (jobId, message) => {
      setJob((prev) =>
        prev && prev.jobId === jobId
          ? { ...prev, status: "error", error: message }
          : prev,
      );
      notifyError(message);
      stopPolling();
    },
    [notifyError, stopPolling],
  );

  const poll = useCallback(
    (jobId, startedAt, misses) => {
      pollRef.current.timer = setTimeout(async () => {
        if (pollRef.current.jobId !== jobId) return; // superseded
        if (Date.now() - startedAt > POLL_MAX_MS) {
          failJob(jobId, "Drafting took too long - please try again.");
          return;
        }
        let data = null;
        try {
          const res = await axios.get(
            `${API}/api/proposals/intake/draft-reply/${jobId}`,
          );
          data = res.data;
        } catch {
          // Transient poll failure (edge blip, brief redeploy) - the
          // job keeps running server-side, tolerate a few misses.
          const nextMisses = misses + 1;
          if (nextMisses >= POLL_MAX_MISSES) {
            failJob(
              jobId,
              "Lost connection while the draft was being written - please try again.",
            );
            return;
          }
          poll(jobId, startedAt, nextMisses);
          return;
        }
        if (data.status === "done") {
          setJob((prev) =>
            prev && prev.jobId === jobId
              ? { ...prev, status: "done", result: data }
              : prev,
          );
          notifyDone();
          stopPolling();
          return;
        }
        if (data.status === "error") {
          failJob(jobId, data.error || "Couldn't draft the reply.");
          return;
        }
        poll(jobId, startedAt, 0);
      }, POLL_INTERVAL_MS);
    },
    [failJob, notifyDone, stopPolling],
  );

  /**
   * Kick off a draft job. `payload` goes to POST /draft-reply;
   * `mintResult` + `parsedMsg` ride along in context state so the
   * wizard can resume at the draft step after navigation. Throws on
   * a start failure - the caller owns that toast.
   */
  const startDraftJob = useCallback(
    async ({ payload, mintResult, parsedMsg }) => {
      const { data } = await axios.post(
        `${API}/api/proposals/intake/draft-reply`,
        payload,
      );
      const jobId = data.job_id;
      stopPolling();
      ctxRef.current = {
        projectReference: mintResult?.project_reference || "",
      };
      pollRef.current.jobId = jobId;
      setJob({
        jobId,
        status: "pending",
        startedAt: Date.now(),
        mintResult,
        parsedMsg,
        result: null,
        error: null,
      });
      poll(jobId, Date.now(), 0);
    },
    [poll, stopPolling],
  );

  const value = useMemo(
    () => ({ job, startDraftJob, clearJob }),
    [job, startDraftJob, clearJob],
  );
  return <DraftJobCtx.Provider value={value}>{children}</DraftJobCtx.Provider>;
}

export function useDraftJob() {
  return useContext(DraftJobCtx);
}
